import { useState, useRef, useId, useLayoutEffect } from 'react'
import { faTicket, faPaperPlane } from '@fortawesome/pro-solid-svg-icons'
import { Icon, Button, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { AttachmentDropZone } from './AttachmentDropZone'
import { UploadingHint } from './UploadingHint'
import { useAttachmentsWithAnalytics } from '../hooks/useAttachmentsWithAnalytics'
import type { AttachmentRef } from '../lib/attachments.types'

interface TicketDraftProps {
  subject: string
  body: string
  disabled?: boolean
  readOnly?: boolean
  attachmentsEnabled?: boolean
  onSubmit: (payload: {
    subject: string
    body: string
    pendingAttachments: AttachmentRef[]
    attachmentFiles: File[]
  }) => void
  onCancel: () => void
}

const MAX_BODY_HEIGHT = 400

export const TicketDraft = ({
  subject,
  body,
  disabled = false,
  readOnly = false,
  attachmentsEnabled = false,
  onSubmit,
  onCancel,
}: TicketDraftProps) => {
  const [editedBody, setEditedBody] = useState(body)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const uploadingHintId = useId()
  const {
    items,
    add,
    retry,
    cancel,
    remove,
    clear,
    refs,
    uploadedFiles,
    isUploading,
    isFull,
    trackSubmitted,
  } = useAttachmentsWithAnalytics()

  // Auto-resize the body to fit content so the full ticket preview is visible
  // without an inner scrollbar, up to a sensible cap.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return undefined
    const fit = () => {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, MAX_BODY_HEIGHT)}px`
    }
    fit()
    // The card mounts hidden while the reply above it types, where scrollHeight
    // is 0, so fit again once it has a width.
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [editedBody])

  const submitDisabled = disabled || isUploading

  const handleSubmit = () => {
    if (attachmentsEnabled) {
      trackSubmitted()
    }
    onSubmit({
      subject,
      body: editedBody,
      pendingAttachments: refs,
      attachmentFiles: uploadedFiles,
    })
  }

  return (
    <div
      className={mtc(
        'my-2 rounded-[24px] overflow-hidden',
        'border border-neutral-200 bg-neutral-0',
        'p-6 flex flex-col gap-4'
      )}
      style={{
        boxShadow:
          '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)',
      }}
    >
      {/* Card header */}
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center size-8 rounded-[8px] bg-moss-25 shrink-0">
          <Icon icon={faTicket} className="text-[16px] text-moss-200" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
            New ticket preview
          </Text>
          <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
            {subject}
          </Text>
        </div>
      </div>

      {/* Editable body */}
      <textarea
        ref={textareaRef}
        value={editedBody}
        onChange={(e) => setEditedBody(e.target.value)}
        rows={6}
        disabled={disabled || readOnly}
        aria-label="Ticket body"
        style={{ maxHeight: MAX_BODY_HEIGHT }}
        className={mtc(
          'w-full rounded-[8px] border border-neutral-200 bg-neutral-0',
          'px-4 py-3 text-sm text-neutral-700 leading-relaxed',
          'resize-none focus:outline-none focus:border-neutral-300',
          'shadow-[0_1px_2px_rgba(0,0,0,0.06)]',
          (disabled || readOnly) && 'cursor-not-allowed'
        )}
      />

      {attachmentsEnabled && !readOnly && (
        <AttachmentDropZone
          items={items}
          onAdd={add}
          onRetry={retry}
          onCancel={cancel}
          onRemove={remove}
          disabled={disabled}
          isFull={isFull}
        />
      )}

      {!readOnly && (
        <div className="flex flex-col items-end gap-2">
          {isUploading && <UploadingHint id={uploadingHintId} />}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clear()
                onCancel()
              }}
              disabled={disabled}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconLeft={faPaperPlane}
              onClick={handleSubmit}
              disabled={submitDisabled}
              aria-describedby={isUploading ? uploadingHintId : undefined}
            >
              Submit Ticket
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
